import {
  groupBy,
  sumBy,
  isEmpty,
  get,
  minBy,
  round,
  sum,
  mapValues,
  keyBy,
  isNumber,
  startCase,
} from 'lodash'

import {
  getProficiencyBand,
  percentage,
  getSelectedPerformanceBand,
  getShortTestName,
  formatDate,
} from '@edulastic/constants/reportUtils/common'
import {
  getAchievementLevels,
  getScoreLabel,
} from '@edulastic/constants/const/dataWarehouse'
import {
  EXTERNAL_SCORE_TYPES,
  compareByOptionsInfo,
  getExternalScoreFormattedByType,
  getTestUniqId,
  compareByKeys,
} from '../common/utils'
import { getAllTestTypeLabels } from '../../../../src/utils/testTypes'
import { getSelectedExternalScore } from '../wholeLearnerReport/utils'

/**
 * NOTE: the constants declared here affects only the Multiple Assessment Report in Data Warehouse
 */

export const ANALYZE_BY_AVERAGE_SCORE_TABLE_PAGE_SIZE = 100
export const ANALYZE_BY_PERFORMANCE_DISTRIBUTION_TABLE_PAGE_SIZE = 25

export const INITIAL_FILTERS = {
  reportId: '',
  termId: '',
  districtIds: '',
  networkIds: '',
  schoolIds: '',
  teacherIds: '',
  grades: '',
  subjects: '',
  courseId: 'All',
  classIds: '',
  groupIds: '',
  assignedBy: 'anyone',
  testTermIds: '',
  assessmentTypes: '',
  testGrades: '',
  testSubjects: '',
  tagIds: '',
  testUniqIds: '',
  profileId: '',
  externalScoreType: 'scaledScore',
  race: 'all',
  gender: 'all',
  iepStatus: 'all',
  frlStatus: 'all',
  ellStatus: 'all',
  hispanicEthnicity: 'all',
}

export const REQUEST_FILTERS = {
  reportId: '',
  termId: '',
  districtIds: '',
  networkIds: '',
  schoolIds: '',
  teacherIds: '',
  subjects: '',
  grades: '',
  courseId: '',
  classIds: '',
  groupIds: '',
  testTermIds: '',
  testUniqIds: '',
  testSubjects: '',
  testGrades: '',
  assessmentTypes: '',
  tagIds: '',
  profileId: '',
  race: 'all',
  gender: 'all',
  iepStatus: 'all',
  frlStatus: 'all',
  ellStatus: 'all',
  hispanicEthnicity: 'all',
  externalScoreType: '',
}

export const sortKeys = {
  COMPARE_BY: 'compareBy',
  // DISTRICT: 'districtName',
  ASSESSMENT_: 'ASSESSMENT_',
}

export const compareByMap = mapValues(compareByOptionsInfo, ({ name }) => name)

export const ANALYZE_BY_KEYS = {
  AVERAGE_SCORE: 'averageScore',
  PERFORMANCE_DISTRIBUTION: 'performanceDistribution',
}

export const ANALYZE_BY_OPTIONS = [
  { key: ANALYZE_BY_KEYS.AVERAGE_SCORE, title: 'Average Score' },
  {
    key: ANALYZE_BY_KEYS.PERFORMANCE_DISTRIBUTION,
    title: 'Performance Distribution',
  },
]

export const tableColumnsData = [
  {
    dataIndex: sortKeys.COMPARE_BY,
    key: sortKeys.COMPARE_BY,
    align: 'left',
    fixed: 'left',
    width: 200,
    sorter: true,
  },
  // next up are dynamic columns for each assessment
]

const groupByCompareByKey = (metricInfo, compareBy) => {
  switch (compareBy) {
    case 'district':
      return groupBy(metricInfo, 'districtId')
    case 'school':
      return groupBy(metricInfo, 'schoolId')
    case 'student':
      return groupBy(metricInfo, 'studentId')
    case 'class':
    case 'group':
      return groupBy(metricInfo, 'groupId')
    case 'teacher':
      return groupBy(metricInfo, 'teacherId')
    case 'race':
      return groupBy(metricInfo, 'race')
    case 'gender':
      return groupBy(metricInfo, 'gender')
    case 'ellStatus':
      return groupBy(metricInfo, 'ellStatus')
    case 'iepStatus':
      return groupBy(metricInfo, 'iepStatus')
    case 'hispanicEthnicity':
      return groupBy(metricInfo, 'hispanicEthnicity')
    case 'frlStatus':
      return groupBy(metricInfo, 'frlStatus')
    case 'standard':
      return groupBy(metricInfo, 'standardId')
    default:
      return {}
  }
}

const findTestWithAverageBand = (tests) => {
  const NO_RANK = 0
  const weightedAverageRank =
    sum(
      tests.map((t) => get(t, 'band.rank', NO_RANK) * t.totalStudentCount || 0)
    ) / sumBy(tests, 'totalStudentCount')
  const item = minBy(
    tests,
    (el) => get(el, 'band.rank', NO_RANK) - weightedAverageRank
  )
  return item || {}
}

const getWeightedAchievementLevel = (records) => {
  const weightedAverage =
    sum(
      records.map(
        (record) => (record.achievementLevel || 0) * (record.totalGraded || 0)
      )
    ) / sum(records.map((record) => parseInt(record.totalGraded || 0, 10)))

  return round(weightedAverage || 0, 2)
}

const augmentBandData = (tests, bandInfo, externalBands) => {
  const testsWithBandInfo = tests.map(({ ...t }) => {
    let band = { name: '-', color: '#010101' }
    if (t.externalTestType) {
      const achievementLevels = getAchievementLevels(
        { ...t, title: t.testTitle },
        externalBands
      )
      Object.assign(t, { bands: achievementLevels })
      band = achievementLevels.find((al) => al.active) || band
    } else {
      band = getProficiencyBand(t.averageScorePercentage, bandInfo) || band
    }
    Object.assign(t, { band, distribution: t.distribution || [] })
    return t
  })
  return testsWithBandInfo
}

const getExternalScores = (
  lexileScore,
  quantileScore,
  percentile,
  totalStudentCount
) =>
  [
    [lexileScore, EXTERNAL_SCORE_TYPES.LEXILE_SCORE],
    [quantileScore, EXTERNAL_SCORE_TYPES.QUANTILE_SCORE],
    [percentile, EXTERNAL_SCORE_TYPES.PERCENTILE],
  ].map(([score, scoreType]) => {
    const _score = score / totalStudentCount
    return getExternalScoreFormattedByType({
      externalScore: _score,
      externalScoreType: scoreType,
      formatScore: false,
    })
  })

const getAggregatedDataByTestUniqId = (
  metricInfo,
  filters = {},
  userTestTypesInternal,
  analyzeByKey
) => {
  const { externalScoreType } = filters
  const result = {}
  const groupedByTestUniqId = groupBy(metricInfo, 'testUniqId')
  const isAnalyzeByPerformanceDistribution =
    analyzeByKey === ANALYZE_BY_KEYS.PERFORMANCE_DISTRIBUTION

  Object.keys(groupedByTestUniqId).forEach((testUniqId) => {
    const {
      testName = 'N/A',
      isIncomplete = false,
      ...testData
    } = groupedByTestUniqId[testUniqId].reduce(
      (res, ele) => {
        const _isIncomplete = ele.isIncomplete || res.isIncomplete
        const _res =
          parseInt(ele.assessmentDate, 10) > res.assessmentDate ? ele : res
        return {
          ..._res,
          assessmentDate: parseInt(_res.assessmentDate, 10),
          totalTotalScore:
            (parseFloat(ele.totalScore, 10) || 0) + res.totalTotalScore,
          totalTotalLexileScore:
            parseFloat(ele.totalLexileScore, 10) + res.totalTotalLexileScore,
          totalTotalQuantileScore:
            parseFloat(ele.totalQuantileScore, 10) +
            res.totalTotalQuantileScore,
          totalPercentile:
            parseFloat(ele.totalPercentile, 10) + res.totalPercentile,
          totalMaxScore:
            (parseFloat(ele.totalMaxScore, 10) || 0) + res.totalMaxScore,
          totalStudentCount:
            (parseInt(ele.totalStudentCount, 10) || 0) +
            parseInt(res.totalStudentCount, 10),
          isIncomplete: _isIncomplete,
        }
      },
      {
        assessmentDate: 0,
        totalTotalScore: 0,
        totalTotalLexileScore: 0,
        totalTotalQuantileScore: 0,
        totalPercentile: 0,
        totalMaxScore: 0,
        totalStudentCount: 0,
      }
    )

    // this will return empty object for analyze by performance distribution
    const { band, bands } = !isAnalyzeByPerformanceDistribution
      ? findTestWithAverageBand(groupedByTestUniqId[testUniqId])
      : {}
    if (band) testData.band = band
    if (bands) testData.bands = bands

    const averageScaledScore =
      testData.totalTotalScore /
        (testData.externalTestType
          ? testData.totalStudentCount
          : testData.totalMaxScore) || 0

    const [
      averageLexileScore,
      averageQuantileScore,
      averagePercentile,
    ] = getExternalScores(
      testData.totalTotalLexileScore,
      testData.totalTotalQuantileScore,
      testData.totalPercentile,
      testData.totalStudentCount
    )

    let averageScore = averageScaledScore
    if (testData.externalTestType) {
      averageScore = getSelectedExternalScore({
        score: averageScaledScore,
        lexileScore: averageLexileScore,
        quantileScore: averageQuantileScore,
        percentile: averagePercentile,
        externalScoreType,
      })
    }
    // mix of averageScore(total/count) & averageFractionalScore(total/max)
    const _testName = testData.externalTestType
      ? testName
      : `${testName} (${
          getAllTestTypeLabels(userTestTypesInternal)[testData.testType]
        })`
    result[testUniqId] = {
      ...testData,
      testName: _testName,
      isIncomplete,
      averageScore,
      averageScorePercentage: round(averageScaledScore * 100),
      averageScaledScore,
      averageLexileScore,
      averageQuantileScore,
      averagePercentile,
    }
  })
  return result
}

/**
 * Method to generate table data from table and chart api response to render table.
 * @param {Record[]} reportTableData - table api response.
 * @param {Record[]} reportChartData - chart api response.
 * @param {Record[]} feedTypes - contains supported feedTypes for the ditrict.
 * @param {Record[]} bandInfo - contains performance bands data.
 * @param {string} compareByKey - selected compare by filter key in the table.
 * @param {Record} sortFilters - contains sort filters data.
 * @param {Record} filters - contains either shared report filters or popup filters data.
 * @returns {Record[]} - transformed data for rendering the table.
 */
export const getTableData = (
  orgData,
  reportTableData,
  filtersData,
  feedTypes,
  bandInfo = [],
  compareByKey,
  analyzeByKey,
  sortFilters,
  filters = {},
  isDistrictGroupAdmin,
  userTestTypesInternal
) => {
  const { metricInfo = [] } = get(reportTableData, 'data.result', {})
  const { externalBands = [] } = get(filtersData, 'data.result', {})
  const feedTypesMap = keyBy(feedTypes, 'key')
  const { districts: districtsInfo = [] } = orgData?.districtGroup || {}

  const filteredMetricInfo = metricInfo
    .map(({ ...item }) => {
      const testUniqId = getTestUniqId(item)
      const isExternal = !!feedTypesMap[item.testType]

      if (isExternal) {
        // map data for external test type
        const districtName =
          isDistrictGroupAdmin && !item.districtName
            ? districtsInfo.find((d) => d._id === item.districtId)?.name
            : item.districtName
        Object.assign(item, {
          externalTestType: item.testType,
          externalTestTypeLabel: feedTypesMap[item.testType].title,
          districtName,
          assessmentDate: +new Date(item.assessmentDate),
        })
      } else {
        // map data for internal tests
        const averageScorePercentage =
          (item.totalScore * 100) / item.totalMaxScore || 0
        Object.assign(item, { averageScorePercentage })
      }

      return { ...item, testUniqId, title: item.testTitle }
    })
    .filter(
      ({ externalTestType }) =>
        (!isEmpty(bandInfo) && !externalTestType) ||
        (!isEmpty(externalBands) && externalTestType)
    )

  const metricInfoWithBandData = augmentBandData(
    filteredMetricInfo,
    bandInfo,
    externalBands
  )

  // table data for each assessment
  const groupedByCompareByKey = groupByCompareByKey(
    metricInfoWithBandData,
    compareByKey
  )
  const compareByLabelKey = compareByMap[compareByKey]
  const tableData = Object.keys(groupedByCompareByKey).map((compareByValue) => {
    const rowData = groupedByCompareByKey[compareByValue]
    const compareByLabelValue = rowData[0][compareByLabelKey]
    const tests = getAggregatedDataByTestUniqId(
      rowData,
      filters,
      userTestTypesInternal,
      analyzeByKey
    )
    return {
      id: compareByValue,
      [compareByKey]: compareByValue,
      [compareByLabelKey]: compareByLabelValue || '',
      districtId: rowData[0].districtId,
      districtName: rowData[0].districtName,
      tests,
    }
  })

  return tableData
}

const getInternalChartData = (
  internalMetricInfo,
  bandInfo,
  userTestTypesInternal
) => {
  const internalGroupedByTestUniqId = groupBy(internalMetricInfo, 'testUniqId')
  const internalChartData = Object.keys(internalGroupedByTestUniqId).map(
    (testUniqId) => {
      const records = internalGroupedByTestUniqId[testUniqId]
      const [defaultTestData] = records
      const {
        testName = 'N/A',
        isIncomplete = false,
        ...testData
      } = records.reduce(
        (res, ele) => {
          const _isIncomplete = ele.isIncomplete || res.isIncomplete
          const _res =
            parseInt(ele.assessmentDate, 10) > res.assessmentDate ? ele : res
          const _totalGraded = parseInt(ele.totalGraded || 0, 10) || 0
          const _totalScore = parseFloat(ele.totalScore || 0, 10) || 0
          const _totalMaxScore =
            parseFloat(ele.maxPossibleScore || 0) * _totalGraded
          return {
            ..._res,
            assessmentDate: parseInt(_res.assessmentDate, 10),
            isIncomplete: _isIncomplete,
            totalGraded: res.totalGraded + _totalGraded,
            totalScore: res.totalScore + _totalScore,
            totalMaxScore: res.totalMaxScore + _totalMaxScore,
          }
        },
        {
          ...defaultTestData,
          assessmentDate: 0,
          totalGraded: 0,
          totalScore: 0,
          totalMaxScore: 0,
        }
      )

      const averageScore = percentage(
        testData.totalScore,
        testData.totalMaxScore
      )
      const averageScoreBand =
        bandInfo.find((band) => band.threshold < averageScore) || bandInfo[0]

      // curate records for each performance band
      const _records = bandInfo.map((band) => {
        const _default = {
          totalGraded: 0,
          totalGradedPercentage: 0,
          threshold: band.threshold,
          color: band.color,
          bandName: band.name,
          aboveStandard: band.aboveStandard,
        }
        const _record = records.find((r) => r.bandScore == band.threshold) || {}
        if (parseInt(_record.totalGraded, 10)) {
          _record.totalGradedPercentage = round(
            percentage(_record.totalGraded, testData.totalGraded),
            2
          )
        }
        return { ..._default, ..._record }
      })

      return {
        ...testData,
        testUniqId,
        testName: `${testName} (${
          getAllTestTypeLabels(userTestTypesInternal)[testData.testType]
        })`,
        isIncomplete,
        totalScore: round(testData.totalScore, 2),
        averageScore: round(averageScore, 2),
        maxPossibleScore: (
          records.find((r) => r.maxPossibleScore) || records[0]
        ).maxPossibleScore,
        records: _records,
        bandName: averageScoreBand.name,
        color: averageScoreBand.color,
      }
    }
  )
  return internalChartData
}

const getExternalChartData = (
  externalMetricInfo,
  externalBands,
  externalScoreType
) => {
  const externalGroupedByTestUniqId = groupBy(externalMetricInfo, 'testUniqId')
  const externalChartData = Object.keys(externalGroupedByTestUniqId).map(
    (testUniqId) => {
      const records = externalGroupedByTestUniqId[testUniqId]
      const testData = externalGroupedByTestUniqId[testUniqId].reduce(
        (res, ele) => {
          const _res =
            parseInt(ele.assessmentDate, 10) > res.assessmentDate ? ele : res
          const _totalGraded = parseInt(ele.totalGraded || 0, 10) || 0
          const _totalScore = parseFloat(ele.totalScore || 0, 10) || 0
          const _totalLexileScore = parseFloat(ele.totalLexileScore, 10)
          const _totalQuantileScore = parseFloat(ele.totalQuantileScore, 10)
          const _totalPercentile = parseFloat(ele.totalPercentile, 10)
          return {
            ..._res,
            assessmentDate: parseInt(_res.assessmentDate, 10),
            totalGraded: res.totalGraded + _totalGraded,
            totalScore: res.totalScore + _totalScore,
            totalLexileScore: res.totalLexileScore + _totalLexileScore,
            totalQuantileScore: res.totalQuantileScore + _totalQuantileScore,
            totalPercentile: res.totalPercentile + _totalPercentile,
          }
        },
        {
          assessmentDate: 0,
          totalGraded: 0,
          totalScore: 0,
          totalLexileScore: 0,
          totalQuantileScore: 0,
          totalMaxScore: 0,
          totalLexileMaxScore: 0,
          totalQuantileMaxScore: 0,
          totalPercentile: 0,
        }
      )
      testData.achievementLevel = getWeightedAchievementLevel(
        externalGroupedByTestUniqId[testUniqId]
      )

      const averageScaledScore = testData.totalScore / testData.totalGraded || 0
      const [
        averageLexileScore,
        averageQuantileScore,
        averagePercentile,
      ] = getExternalScores(
        testData.totalLexileScore,
        testData.totalQuantileScore,
        testData.totalPercentile,
        testData.totalGraded
      )
      const averageScore = getSelectedExternalScore({
        score: averageScaledScore,
        lexileScore: averageLexileScore,
        quantileScore: averageQuantileScore,
        percentile: averagePercentile,
        externalScoreType,
      })

      // curate records for each performance criteria of external test
      let _records = []
      testData.title = testData.testTitle
      const _achievementLevels = getAchievementLevels(testData, externalBands)
      testData.bands = _achievementLevels
      testData.band = _achievementLevels.find((al) => al.active)
      _records = _achievementLevels.map((band) => {
        const _default = {
          totalGraded: 0,
          totalGradedPercentage: 0,
          id: band.id,
          color: band.color,
          bandName: band.name,
        }
        const _recordsWithBands = augmentBandData(records, null, externalBands)
        const _record =
          _recordsWithBands.find((r) => r?.band?.id == band.id) || {}
        if (parseInt(_record.totalGraded, 10)) {
          _record.totalGradedPercentage = round(
            percentage(_record.totalGraded, testData.totalGraded),
            2
          )
        }
        return { ..._default, ..._record }
      })

      return {
        ...testData,
        testUniqId,
        averageScore,
        averageScaledScore,
        averageLexileScore,
        averageQuantileScore,
        averagePercentile,
        records: _records,
      }
    }
  )
  return externalChartData
}

/**
 * Method to generate transformed chart data for rendering the chart.
 * @param {Record[]} internalMetricsForChart - contains internal test metrics.
 * @param {Record[]} externalMetricsForChart - contains external test metrics.
 * @param {Record[]} bandInfo - contains internal tests performance band info.
 * @param {Record[]} externalBands - contains external tests performance band info.
 * @param {Record} filters - contains either shared report filters or popup filters data.
 * @param {Object.<string, object>} termsKeyedById - contains terms with termId as the key
 * @returns {Record[]} - transformed chart data to render chart.
 */
export const getChartData = (
  internalMetricsForChart = [],
  externalMetricsForChart = [],
  bandInfo = [],
  externalBands = [],
  filters = {},
  termsKeyedById = {},
  userTestTypesInternal
) => {
  const { externalScoreType } = filters
  const externalBandsMapped = keyBy(externalBands, 'testCategory')
  // fallback to prevent intermittent crashes when bandInfo is empty
  const internalMetricInfo = !isEmpty(bandInfo)
    ? internalMetricsForChart.map((t) => {
        const selectedTerm = termsKeyedById[t.termId]
        return {
          ...t,
          testUniqId: getTestUniqId(t),
          termName: selectedTerm?.name,
          termEndDate: selectedTerm?.endDate,
        }
      })
    : []
  // filter out external tests data without achievement level
  const externalMetricInfo = externalMetricsForChart
    .filter((t) => t.testType && t.achievementLevel)
    .map((t) => {
      const selectedTerm = termsKeyedById[t.termId]
      return {
        ...t,
        shortTestName: getShortTestName(t.testType, t.testName),
        externalTestType: t.testType,
        externalTestTypeLabel:
          externalBandsMapped[t.testType]?.testCategoryLabel ?? t.testType,
        assessmentDate: +new Date(t.assessmentDate),
        achievementLevel: +t.achievementLevel,
        testUniqId: getTestUniqId(t),
        termName: selectedTerm?.name,
        termEndDate: selectedTerm?.endDate,
      }
    })
  if (isEmpty(internalMetricInfo) && isEmpty(externalMetricInfo)) {
    return []
  }

  // curate chart data for internal tests
  const internalChartData = getInternalChartData(
    internalMetricInfo,
    bandInfo,
    userTestTypesInternal
  )

  // curate chart data for external tests
  const externalChartData = getExternalChartData(
    externalMetricInfo,
    externalBands,
    externalScoreType
  )

  // sort assessments by date and alphabetically
  const chartData = [...internalChartData, ...externalChartData].sort(
    (a, b) =>
      a.assessmentDate - b.assessmentDate ||
      a.testName.toLowerCase().localeCompare(b.testName.toLowerCase())
  )

  return chartData
}

/**
 * Method to get specific data i.e. incompleteTests, selectedPerformanceBand and chartData.
 * @param {Record[]} filtersData - filter api response.
 * @param {Record} sharedReportFilters -  shared report filters data for the shared reports.
 * @param {Record} settings - popup filters data.
 * @param {Record[]} reportChartData - chart api response.
 * @returns {Record} containing incompleteTests, selectedPerformanceBand and chartData.
 */

export const getChartSpecifics = (
  filtersData,
  sharedReportFilters,
  settings,
  reportChartData,
  orgData,
  userTestTypesInternal
) => {
  const { terms = [], districtGroup = {} } = orgData
  const { sauTerms = [], stateTerms = [] } = districtGroup || {}
  const districtGroupTerms = [...sauTerms, ...stateTerms]
  const termsKeyedById = keyBy([...terms, ...districtGroupTerms], '_id')

  // performance band for chart should update post chart data API response
  const { bandInfo = [], externalBands = [] } = get(
    filtersData,
    'data.result',
    {}
  )

  const profileId = (sharedReportFilters || settings.requestFilters).profileId
  const selectedPerformanceBand = getSelectedPerformanceBand(
    bandInfo,
    profileId
  )

  // curate chart data from API response
  const { internalMetricsForChart = [], externalMetricsForChart = [] } = get(
    reportChartData,
    'data.result',
    {}
  )
  const chartData = getChartData(
    internalMetricsForChart,
    externalMetricsForChart,
    selectedPerformanceBand,
    externalBands,
    sharedReportFilters || settings.requestFilters,
    termsKeyedById,
    userTestTypesInternal
  )

  return {
    selectedPerformanceBand,
    chartData,
  }
}

export function getTestName(data) {
  const { isIncomplete, testName, externalTestType, shortTestName } = data
  const _testName = externalTestType ? shortTestName : testName
  return isIncomplete ? `${_testName} *` : _testName
}

export function buildChartData(
  chartData,
  barsDataForExternal,
  barsDataForInternal
) {
  return chartData.map((d) => {
    if (d.externalTestType) {
      // for external assessments
      const filteredBarsDataForExternal = barsDataForExternal.filter(
        (b) => b.testUniqId === d.testUniqId
      )
      const barsCellDataForExternal = filteredBarsDataForExternal.reduce(
        (res, ele) => {
          const barData = d.records.find((r) => r.id == ele.id)
          res[ele.key] = barData.totalGradedPercentage
          res[ele.insideLabelKey] =
            barData.totalGradedPercentage >= 10
              ? `${round(barData.totalGradedPercentage, 0) || 0}%`
              : ''
          return res
        },
        {}
      )
      const barsCellAdditionalDataForExternal = barsDataForExternal.reduce(
        (res, ele) => {
          res[ele.key] = { fill: ele.fill }
          return res
        },
        {}
      )
      const _topLabelKey = `top-label-bar${barsDataForExternal.length}`
      const score = isNumber(d.averageScore)
        ? round(d.averageScore || 0)
        : d.averageScore
      const topLabelValue = getScoreLabel(score, d)
      return {
        ...d,
        ...barsCellDataForExternal,
        [_topLabelKey]: topLabelValue,
        additionalData: barsCellAdditionalDataForExternal,
      }
    }
    // for internal assessments
    const barsCellDataForInternal = barsDataForInternal.reduce((res, ele) => {
      const barData = d.records.find((r) => r.threshold == ele.threshold)
      res[ele.key] = barData.totalGradedPercentage
      res[ele.insideLabelKey] =
        barData.totalGradedPercentage >= 10
          ? `${round(barData.totalGradedPercentage, 0) || 0}%`
          : ''
      return res
    }, {})
    const barsCellAdditionalDataForInternal = barsDataForInternal.reduce(
      (res, ele) => {
        res[ele.key] = { fill: ele.fill }
        return res
      },
      {}
    )
    const _topLabelKey = `top-label-bar${barsDataForInternal.length}`
    return {
      ...d,
      ...barsCellDataForInternal,
      [_topLabelKey]: `${round(d.averageScore, 0)}%`,
      additionalData: barsCellAdditionalDataForInternal,
    }
  })
}

export const getDownloadCsvColumnHeadersFunc = (
  settings,
  overallAssessmentsData,
  userTestTypesInternal,
  isDistrictGroupAdmin
) => () => {
  const {
    key: compareByKey,
    title: compareByTitle,
  } = settings.selectedCompareBy

  const isAnalyzeByPerformanceDistribution =
    settings.selectedAnalyzeBy.key === ANALYZE_BY_KEYS.PERFORMANCE_DISTRIBUTION

  const dowloadCsvTableColumnHeaders = {
    names: [compareByTitle],
    dates: ['Date'],
    testType: ['Test Type'],
    totalStudents: ['Students'],
    avgScore: ['Avg. Score'],
  }

  if (
    isDistrictGroupAdmin &&
    [compareByKeys.SCHOOL, compareByKeys.TEACHER, compareByKeys.CLASS].includes(
      compareByKey
    )
  ) {
    const districtColumnTitle = startCase(compareByMap[compareByKeys.DISTRICT])
    dowloadCsvTableColumnHeaders.names.push(districtColumnTitle)
    dowloadCsvTableColumnHeaders.dates.push('-')
    dowloadCsvTableColumnHeaders.testType.push('-')
    dowloadCsvTableColumnHeaders.totalStudents.push('-')
    dowloadCsvTableColumnHeaders.avgScore.push('-')
  }

  const addAdditionalColumns = (assessment) => {
    const {
      assessmentDate,
      totalGraded,
      testType,
      externalTestType,
      externalTestTypeLabel,
      averageScore,
    } = assessment
    const scoreLabel = isNumber(averageScore)
      ? getScoreLabel(round(averageScore), { externalTestType })
      : averageScore
    dowloadCsvTableColumnHeaders.dates.push(formatDate(assessmentDate))
    dowloadCsvTableColumnHeaders.testType.push(
      externalTestTypeLabel ||
        getAllTestTypeLabels(userTestTypesInternal)[testType]
    )
    dowloadCsvTableColumnHeaders.totalStudents.push(`${totalGraded}`)
    dowloadCsvTableColumnHeaders.avgScore.push(scoreLabel)
  }

  overallAssessmentsData.forEach((assessment) => {
    const { externalTestType } = assessment
    const _testName = getTestName(assessment)
    const csvTableColumnHeader = isAnalyzeByPerformanceDistribution
      ? `${_testName} - Performance Distribution`
      : `${_testName}${externalTestType ? ' - Score' : ' - Score(%)'}`
    dowloadCsvTableColumnHeaders.names.push(csvTableColumnHeader)
    addAdditionalColumns(assessment)
  })
  if (!isAnalyzeByPerformanceDistribution) {
    overallAssessmentsData.forEach((assessment) => {
      const _testName = getTestName(assessment)
      dowloadCsvTableColumnHeaders.names.push(`${_testName} - Performance Band`)
      addAdditionalColumns(assessment)
    })
  }
  return dowloadCsvTableColumnHeaders
}
